import type React from "react"

import type { BoxProps } from "./Box"
import type { HeadingProps } from "./Heading"
import type { IconProp } from "./icons"
import type { AcceptsChildren, NoChildren } from "./types"
import type { ViewProps } from "./View"

import { renderIconFromIconProp } from "@/theme/icons/renderIconFromIconProp"
import { AppText } from "@/theme/Text"
import { SPACING } from "@/theme/theme"

import { Box } from "./Box"
import { Heading } from "./Heading"
import { Row } from "./Row"
import { ICON_SIZES } from "./theme/icon-sizes"

export interface TitleWithIconProps extends NoChildren<BoxProps> {
  IconComponent?: IconProp | null
  _headingProps?: HeadingProps
  colorScheme?: HeadingProps["colorScheme"]
  headingVariant?: HeadingProps["variant"]
  helpElement?: React.JSX.Element | null
  rightElement?: React.JSX.Element | string | null
  subheaderElement?: React.JSX.Element | string | null
  titleText: string
  variant?: "pageTitle"
}

/**
 * A title with an icon on the left and an optional element on the right.
 */
export function TitleWithIcon({
  _headingProps,
  colorScheme,
  headingVariant = "h4",
  helpElement,
  IconComponent,
  id,
  rightElement,
  subheaderElement,
  titleText,
  ...rest
}: TitleWithIconProps & ViewProps): React.JSX.Element {
  const iconElement = renderIconFromIconProp(IconComponent, {
    colorScheme,
    size: ICON_SIZES.$lg,
  })

  let elementRight: React.JSX.Element | null = null

  if (typeof rightElement === "string") {
    elementRight = (
      <Heading colorScheme={"secondary"} variant={headingVariant}>
        {rightElement}
      </Heading>
    )
  } else if (rightElement) {
    elementRight = rightElement
  }

  return (
    <Box id={id} {...rest}>
      <Row flexWrap={"nowrap"} justifyContent={"space-between"}>
        <Row flex={1} flexWrap={"nowrap"}>
          {iconElement ? <Box mr={"$2"}>{iconElement}</Box> : null}
          <Heading
            isTruncated
            colorScheme={colorScheme}
            flex={1}
            textTransform={"capitalize"}
            variant={headingVariant}
            {..._headingProps}
          >
            {titleText}
          </Heading>
          {helpElement ? <Box ml={"$2"}>{helpElement}</Box> : null}
        </Row>
        {elementRight ? <Box ml={"$2"}>{elementRight}</Box> : null}
      </Row>
      {typeof subheaderElement === "string" ? (
        <Heading colorScheme={"secondary"} variant={"h5"}>
          {subheaderElement}
        </Heading>
      ) : (
        subheaderElement
      )}
    </Box>
  )
}

export function ScreenSubheader({ children }: AcceptsChildren) {
  return (
    <AppText colorScheme={"secondary"} style={{ marginBottom: SPACING.$4 }}>
      {children}
    </AppText>
  )
}
